/**
 * OrgSitePage — public renderer for the org_site_cms engine.
 *
 * Loads the site by either:
 *   • URL slug param  (e.g. /abmhc → /api/sites/public/abmhc)
 *   • Current hostname (subdomain or custom domain → /api/sites/public/by-host/<host>)
 *
 * Walks the `blocks` array and dispatches each to a block renderer. Unknown
 * block types fall through silently so a future CMS schema change can ship
 * blocks the frontend doesn't recognise yet without breaking the page.
 */
import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Heart, Mail, Phone, MapPin, Loader2, Calendar, Radio, HandHeart, Flame, CheckCircle2, Clock, Wrench, Image as ImageIcon, FileText, MessageSquare, Hand, Bell, Stethoscope, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { formatCurrency as fmtCurrency } from '@/lib/currency';
import ABMHCFeaturedMedia from '@/components/abmhc/ABMHCFeaturedMedia';
import { MediaUpload } from '@/components/MediaUpload';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============== Block Renderers ==============

const Section = ({ children, className = '', theme, ...rest }) => (
  <section className={`py-16 px-4 ${className}`} {...rest}>
    <div className="max-w-5xl mx-auto" style={theme ? { color: theme.text } : undefined}>
      {children}
    </div>
  </section>
);

const HeroBlock = ({ props, theme }) => {
  // Background can be an image OR an mp4/webm. Detect by extension so an admin
  // can drop in a hospital walkthrough video without any code changes.
  const isVideoBg = !!props.image_url && /\.(mp4|webm|mov)(\?|$)/i.test(props.image_url);
  return (
    <section
      id="hero"
      className="relative min-h-[78vh] flex items-center overflow-hidden"
      style={!isVideoBg ? {
        backgroundImage: props.image_url
          ? `linear-gradient(rgba(15,23,42,0.55), rgba(15,23,42,0.65)), url(${props.image_url})`
          : `linear-gradient(135deg, ${theme.primary} 0%, #0f1d3a 100%)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      } : { backgroundColor: '#0f1d3a' }}
      data-testid="org-block-hero"
    >
      {isVideoBg && (
        <>
          <video
            src={props.image_url}
            autoPlay muted loop playsInline
            className="absolute inset-0 w-full h-full object-cover"
          />
          {/* Dark overlay matches the image-mode gradient so the headline stays readable. */}
          <div className="absolute inset-0 bg-slate-900/55 pointer-events-none" />
        </>
      )}
      <div className="max-w-5xl mx-auto px-6 text-white relative z-10">
        {props.kicker && (
          <div className="inline-block mb-6">
            <div className="h-0.5 w-12 mb-3" style={{ backgroundColor: theme.accent }} />
            <p className="uppercase tracking-[0.2em] text-xs font-semibold" style={{ color: theme.accent }}>
              {props.kicker}
            </p>
          </div>
        )}
        <h1 className="font-serif font-bold leading-[1.05] text-4xl sm:text-5xl lg:text-7xl mb-6 max-w-3xl">
          {props.title}
        </h1>
        {props.tagline && (
          <p className="text-base sm:text-lg text-white/85 mb-8 max-w-xl leading-relaxed">
            {props.tagline}
          </p>
        )}
        <div className="flex flex-wrap gap-3">
          {props.cta_href && props.cta_label && (
            <Link to={props.cta_href}>
              <Button size="lg" className="text-white px-6 h-12" style={{ backgroundColor: theme.accent }}>
                {props.cta_label} <Heart className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          )}
          {props.secondary_cta_href && props.secondary_cta_label && (
            <Link to={props.secondary_cta_href}>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 h-12">
                {props.secondary_cta_label}
              </Button>
            </Link>
          )}
        </div>
      </div>
    </section>
  );
};

const AboutBlock = ({ props, theme }) => (
  <Section className="bg-white">
    <div className="grid md:grid-cols-2 gap-8 items-center">
      <div>
        <h2 className="text-3xl font-bold mb-4" style={{ color: theme.primary }}>{props.heading}</h2>
        <p className="text-slate-700 leading-relaxed whitespace-pre-line">{props.body}</p>
      </div>
      {props.image_url && (
        <img src={props.image_url} alt={props.heading} className="rounded-lg shadow-md w-full h-auto" />
      )}
    </div>
  </Section>
);

const ServicesBlock = ({ props, theme }) => (
  <Section className="bg-slate-50">
    <h2 className="text-3xl font-bold text-center mb-10" style={{ color: theme.primary }}>{props.heading}</h2>
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {(props.items || []).map((it, i) => (
        <Card key={i} className="hover:shadow-md transition">
          <CardContent className="p-6">
            <h3 className="font-semibold text-lg mb-2">{it.name}</h3>
            <p className="text-sm text-slate-600">{it.description}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  </Section>
);

const DoctorsBlock = ({ props, theme }) => {
  const items = props.items || [];
  if (!items.length) return null;
  return (
    <Section className="bg-white">
      <h2 className="text-3xl font-bold text-center mb-10" style={{ color: theme.primary }}>{props.heading}</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {items.map((d, i) => (
          <div key={i} className="text-center">
            {d.photo_url && (
              <img src={d.photo_url} alt={d.name} className="w-32 h-32 rounded-full mx-auto mb-3 object-cover" />
            )}
            <h3 className="font-semibold">{d.name}</h3>
            {d.title && <p className="text-sm text-slate-500">{d.title}</p>}
            {d.bio && <p className="text-xs text-slate-500 mt-2 line-clamp-3">{d.bio}</p>}
          </div>
        ))}
      </div>
    </Section>
  );
};

const PatientInfoBlock = ({ props, theme }) => (
  <Section className="bg-slate-50">
    <h2 className="text-3xl font-bold text-center mb-10" style={{ color: theme.primary }}>{props.heading}</h2>
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-3xl mx-auto">
      {(props.items || []).map((it, i) => (
        <div key={i} className="bg-white rounded-md p-4 shadow-sm">
          <p className="text-xs uppercase text-slate-400 tracking-wide">{it.label}</p>
          <p className="text-slate-800 font-medium mt-1">{it.value}</p>
        </div>
      ))}
    </div>
  </Section>
);

const NewsBlock = ({ props, theme }) => {
  const items = props.items || [];
  if (!items.length) return null;
  return (
    <Section className="bg-white">
      <h2 className="text-3xl font-bold mb-8" style={{ color: theme.primary }}>{props.heading}</h2>
      <div className="space-y-4">
        {items.map((n, i) => (
          <div key={i} className="border-l-4 pl-4 py-2" style={{ borderColor: theme.accent }}>
            <p className="text-xs text-slate-500">{n.date}</p>
            <h3 className="font-semibold">{n.title}</h3>
            {n.body && <p className="text-sm text-slate-600 mt-1">{n.body}</p>}
          </div>
        ))}
      </div>
    </Section>
  );
};

const GalleryBlock = ({ props, theme }) => {
  const items = props.items || [];
  if (!items.length) return null;
  // Detect video items by extension so admins can drop in either media type.
  const isVideo = (url) => !!url && /\.(mp4|webm|mov)(\?|$)/i.test(url);
  return (
    <Section className="bg-slate-50">
      <h2 className="text-3xl font-bold text-center mb-10" style={{ color: theme.primary }}>{props.heading}</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {items.map((m, i) => (
          isVideo(m.url) ? (
            <video
              key={i}
              src={m.url}
              controls
              className="rounded shadow-sm w-full h-32 object-cover bg-black"
              data-testid={`gallery-video-${i}`}
            />
          ) : (
            <a key={i} href={m.url} target="_blank" rel="noreferrer">
              <img src={m.url} alt={m.caption || ''} className="rounded shadow-sm hover:scale-[1.02] transition object-cover w-full h-32" />
            </a>
          )
        ))}
      </div>
    </Section>
  );
};

const DonateBlock = ({ props, theme, siteSlug }) => {
  // Three modes supported:
  //   • `cta_href`         — old behaviour, route to a campaign page
  //   • `stripe_enabled`   — show suggested amounts + Stripe checkout
  //   • `bank_details`     — show offline transfer details below the buttons
  // All three can be enabled simultaneously.
  const suggested = (props.suggested_amounts && props.suggested_amounts.length)
    ? props.suggested_amounts
    : [10, 25, 50, 100, 250];
  const [amount, setAmount] = React.useState(suggested[1] || 25);
  const [custom, setCustom] = React.useState('');
  const [donorName, setDonorName] = React.useState('');
  const [donorEmail, setDonorEmail] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  // Detect Stripe return so we can confirm the session and thank the donor.
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const donation = params.get('donation');
    const sid = params.get('session_id');
    if (donation === 'success' && sid && siteSlug) {
      fetch(`${API_URL}/api/sites/public/${siteSlug}/donate/status/${sid}`)
        .then((r) => r.json())
        .then((d) => {
          if (d.payment_status === 'paid') {
            toast.success('Thank you for your gift! Your donation was received.');
          } else {
            toast.info('Donation pending — we will confirm shortly.');
          }
        })
        .catch(() => { /* silent */ });
    } else if (donation === 'cancelled') {
      toast.info('Donation cancelled. No charges were made.');
    }
  }, [siteSlug]);

  const giveWithStripe = async () => {
    const final = Number(custom) > 0 ? Number(custom) : Number(amount);
    if (!final || final < 1) {
      toast.error('Please choose an amount of at least 1.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/sites/public/${siteSlug}/donate/stripe-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: final,
          currency: (props.currency || 'usd').toLowerCase(),
          donor_name: donorName,
          donor_email: donorEmail,
          origin_url: window.location.origin,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not start checkout');
      window.location.assign(data.url);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-16 px-4 text-white" style={{ backgroundColor: theme.primary }} data-testid="org-block-donate">
      <div className="max-w-3xl mx-auto text-center">
        <h2 className="text-3xl font-bold mb-3">{props.heading}</h2>
        {props.body && <p className="opacity-90 mb-6 max-w-2xl mx-auto">{props.body}</p>}

        {props.stripe_enabled ? (
          <div className="bg-white text-slate-900 rounded-lg p-6 shadow-lg text-left max-w-xl mx-auto">
            <p className="text-xs uppercase tracking-wider text-slate-500 mb-2">Give securely with card</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {suggested.map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => { setAmount(v); setCustom(''); }}
                  data-testid={`donate-amount-${v}`}
                  className={`px-4 py-2 rounded-full border text-sm font-semibold transition ${
                    amount === v && !custom
                      ? 'text-white'
                      : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                  }`}
                  style={amount === v && !custom ? { backgroundColor: theme.accent, borderColor: theme.accent } : undefined}
                >
                  {(props.currency || 'USD').toUpperCase()} {v}
                </button>
              ))}
              <input
                type="number"
                min={1}
                placeholder="Other"
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                className="px-3 py-2 rounded-full border border-slate-300 text-sm w-24 focus:outline-none focus:ring-2"
                style={{ '--tw-ring-color': theme.accent }}
                data-testid="donate-custom-amount"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-2 mb-3">
              <input
                type="text"
                placeholder="Your name (optional)"
                value={donorName}
                onChange={(e) => setDonorName(e.target.value)}
                className="px-3 py-2 rounded border border-slate-300 text-sm"
                data-testid="donate-name"
              />
              <input
                type="email"
                placeholder="Email for receipt (optional)"
                value={donorEmail}
                onChange={(e) => setDonorEmail(e.target.value)}
                className="px-3 py-2 rounded border border-slate-300 text-sm"
                data-testid="donate-email"
              />
            </div>
            <Button
              size="lg"
              onClick={giveWithStripe}
              disabled={loading}
              className="w-full text-white"
              style={{ backgroundColor: theme.accent }}
              data-testid="donate-stripe-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <HandHeart className="h-4 w-4 mr-2" />}
              {loading ? 'Redirecting…' : `Give ${(props.currency || 'USD').toUpperCase()} ${Number(custom) > 0 ? Number(custom) : amount}`}
            </Button>
          </div>
        ) : props.cta_href && (
          <Link to={props.cta_href}>
            <Button size="lg" className="bg-white text-slate-900 hover:bg-slate-100">
              <Heart className="h-5 w-5 mr-2" />
              {props.cta_label || 'Donate Now'}
            </Button>
          </Link>
        )}

        {props.bank_details && (
          <div className="mt-8 bg-white/10 border border-white/20 rounded-lg p-5 max-w-xl mx-auto text-left">
            <p className="text-xs uppercase tracking-widest text-white/70 mb-2">
              {props.bank_heading || 'Or transfer directly'}
            </p>
            <pre className="text-sm whitespace-pre-wrap font-sans leading-relaxed">{props.bank_details}</pre>
            {props.whatsapp_confirm && (
              <a
                href={`https://wa.me/${String(props.whatsapp_confirm).replace(/\D/g, '')}?text=${encodeURIComponent('Hello, I have made a donation via bank transfer. Please find proof of payment attached.')}`}
                target="_blank"
                rel="noreferrer"
                className="inline-block mt-3 text-xs underline opacity-90 hover:opacity-100"
                data-testid="donate-whatsapp-confirm"
              >
                Send proof on WhatsApp →
              </a>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

// ---------- Events block ----------
const EventsBlock = ({ props, theme }) => {
  const items = props.items || [];
  if (!items.length) return null;
  // Sort upcoming first; items without a parseable date go last.
  const sorted = [...items].sort((a, b) => {
    const ad = Date.parse(a.date || '') || 0;
    const bd = Date.parse(b.date || '') || 0;
    return ad - bd;
  });
  return (
    <Section className="bg-white" data-testid="org-block-events">
      <h2 id="events" className="text-3xl font-serif font-bold text-center mb-10" style={{ color: theme.primary }}>{props.heading || 'Upcoming Events'}</h2>
      <div className="space-y-3 max-w-3xl mx-auto">
        {sorted.map((e, i) => (
          <div key={i} className="flex gap-4 bg-white rounded-lg border border-slate-200 p-4 hover:shadow-sm transition">
            <div className="flex flex-col items-center justify-center w-16 h-16 rounded-md text-center" style={{ backgroundColor: `${theme.accent}22`, color: theme.primary }}>
              <Calendar className="h-5 w-5" />
              <p className="text-xs font-semibold mt-1 leading-none">{e.date || 'TBA'}</p>
            </div>
            <div className="flex-1">
              <h3 className="font-semibold" style={{ color: theme.primary }}>{e.title}</h3>
              {e.location && <p className="text-xs text-slate-500 mt-0.5">{e.location}</p>}
              {e.description && <p className="text-sm text-slate-700 mt-2 whitespace-pre-line">{e.description}</p>}
              {e.cta_href && e.cta_label && (
                <a href={e.cta_href} target={e.cta_href.startsWith('http') ? '_blank' : undefined} rel="noreferrer" className="inline-block mt-2 text-xs font-semibold underline" style={{ color: theme.accent }}>
                  {e.cta_label} →
                </a>
              )}
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
};

// ---------- Livestream block ----------
const LivestreamBlock = ({ props, theme }) => {
  if (!props.url) {
    return (
      <Section className="bg-slate-900 text-white text-center" data-testid="org-block-livestream-empty">
        <div id="livestream" />
        <Radio className="h-8 w-8 mx-auto opacity-50 mb-3" />
        <h2 className="text-2xl font-serif font-bold">{props.heading || 'Livestream'}</h2>
        <p className="text-sm text-slate-400 mt-2">We&apos;ll be live soon — check back during service times.</p>
      </Section>
    );
  }

  // Normalise a YouTube watch URL to /embed; pass FB embed through.
  let embed = props.url;
  const yt = embed.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/live\/)([\w-]{6,})/);
  if (yt) embed = `https://www.youtube.com/embed/${yt[1]}?autoplay=0&rel=0`;
  const isFb = embed.includes('facebook.com');
  if (isFb && !embed.includes('plugins/video.php')) {
    embed = `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(props.url)}&show_text=false&autoplay=false`;
  }

  return (
    <Section className="bg-slate-900 text-white" data-testid="org-block-livestream">
      <div className="text-center mb-6">
        <div id="livestream" />
        <h2 className="text-3xl font-serif font-bold inline-flex items-center gap-3 justify-center">
          {props.is_live && (
            <span className="inline-flex items-center gap-1 text-xs uppercase tracking-widest font-semibold text-red-400">
              <Flame className="h-3.5 w-3.5 animate-pulse" /> Live
            </span>
          )}
          {props.heading || 'Watch Live'}
        </h2>
        {props.subhead && <p className="text-sm text-slate-300 mt-2">{props.subhead}</p>}
      </div>
      <div className="aspect-video max-w-4xl mx-auto rounded-lg overflow-hidden border border-white/10">
        <iframe
          title={props.heading || 'Livestream'}
          src={embed}
          allow="autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
          className="w-full h-full"
        />
      </div>
    </Section>
  );
};

// ---------- Prayer requests block ----------
const PrayerRequestsBlock = ({ props, theme, siteSlug }) => {
  const [name, setName] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [isPublic, setIsPublic] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);
  const showWall = !!props.show_public_wall;

  const { data: wall = [], refetch } = useQuery({
    queryKey: ['prayer-wall', siteSlug],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/sites/public/${siteSlug}/prayer-requests`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: showWall,
    staleTime: 30_000,
  });

  const submit = async (e) => {
    e.preventDefault();
    if (message.trim().length < 3) {
      toast.error('Please share at least a few words.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/sites/public/${siteSlug}/prayer-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), message: message.trim(), is_public: isPublic }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Submission failed');
      toast.success(data.message || 'Thank you — we will be praying for you.');
      setName(''); setMessage(''); setIsPublic(false); setSubmitted(true);
      refetch();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const pray = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/sites/public/${siteSlug}/prayer-request/${id}/pray`, { method: 'POST' });
      if (!res.ok) throw new Error('Could not record prayer');
      refetch();
      toast.success('You prayed for this 🙏');
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <Section className="bg-white" data-testid="org-block-prayer">
      <div className="text-center mb-8">
        <h2 id="prayer" className="text-3xl font-serif font-bold" style={{ color: theme.primary }}>{props.heading || 'Prayer Requests'}</h2>
        {props.subhead && <p className="text-sm text-slate-500 mt-2 max-w-2xl mx-auto">{props.subhead}</p>}
      </div>

      <form onSubmit={submit} className="max-w-xl mx-auto bg-slate-50 rounded-lg p-6 space-y-3 border" data-testid="prayer-form">
        {submitted ? (
          <p className="text-sm text-emerald-700">Your prayer request has been received. We are praying with you.</p>
        ) : (
          <>
            <input
              type="text"
              placeholder="Your name (optional)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2"
              style={{ '--tw-ring-color': theme.accent }}
              data-testid="prayer-name"
            />
            <textarea
              placeholder="Share your prayer request…"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              maxLength={2000}
              className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2"
              style={{ '--tw-ring-color': theme.accent }}
              data-testid="prayer-message"
            />
            <label className="flex items-start gap-2 text-xs text-slate-600">
              <input
                type="checkbox"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
                className="mt-0.5"
                data-testid="prayer-public-toggle"
              />
              <span>
                Share this on the public prayer wall (an admin will review first).
                Otherwise, it stays private between you and the pastoral team.
              </span>
            </label>
            <Button
              type="submit"
              disabled={submitting}
              className="text-white w-full"
              style={{ backgroundColor: theme.accent }}
              data-testid="prayer-submit"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {submitting ? 'Sending…' : 'Submit Prayer Request'}
            </Button>
          </>
        )}
      </form>

      {showWall && wall.length > 0 && (
        <div className="mt-12 max-w-3xl mx-auto">
          <h3 className="text-lg font-serif font-bold mb-4 text-center" style={{ color: theme.primary }}>Public Prayer Wall</h3>
          <div className="space-y-3">
            {wall.map((p) => (
              <div key={p.id} className="bg-slate-50 border rounded p-4 flex flex-col sm:flex-row gap-3" data-testid={`prayer-wall-${p.id}`}>
                <div className="flex-1">
                  <p className="text-sm text-slate-700 whitespace-pre-line">{p.message}</p>
                  <p className="text-xs text-slate-500 mt-2">— {p.name}</p>
                </div>
                <button
                  type="button"
                  onClick={() => pray(p.id)}
                  className="self-start text-xs px-3 py-1.5 rounded-full border border-slate-300 hover:bg-white"
                  data-testid={`prayer-pray-${p.id}`}
                >
                  🙏 I prayed · {p.prayed_count || 0}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </Section>
  );
};

const ContactBlock = ({ props, theme }) => (
  <Section className="bg-white">
    <h2 className="text-3xl font-bold text-center mb-10" style={{ color: theme.primary }}>{props.heading}</h2>
    <div className="grid sm:grid-cols-3 gap-6 max-w-3xl mx-auto text-center">
      {props.email && (
        <div><Mail className="h-6 w-6 mx-auto mb-2" style={{ color: theme.primary }} /><p>{props.email}</p></div>
      )}
      {props.phone && (
        <div><Phone className="h-6 w-6 mx-auto mb-2" style={{ color: theme.primary }} /><p>{props.phone}</p></div>
      )}
      {props.address && (
        <div><MapPin className="h-6 w-6 mx-auto mb-2" style={{ color: theme.primary }} /><p>{props.address}</p></div>
      )}
    </div>
    {props.map_embed_url && (
      <div className="mt-8 max-w-4xl mx-auto aspect-video">
        <iframe title="Map" src={props.map_embed_url} className="w-full h-full rounded-lg border-0" loading="lazy" />
      </div>
    )}
  </Section>
);

const FooterBlock = ({ props }) => (
  <footer className="bg-slate-900 text-slate-300 py-8 px-4 text-center text-sm" data-testid="org-block-footer">
    <p>{props.copyright}</p>
    {Array.isArray(props.social_links) && props.social_links.length > 0 && (
      <div className="flex justify-center gap-4 mt-3">
        {props.social_links.map((l, i) => (
          <a key={i} href={l.url} target="_blank" rel="noreferrer" className="hover:text-white">{l.label}</a>
        ))}
      </div>
    )}
  </footer>
);

// ---------- Church-specific block renderers ----------
const LeadershipBlock = ({ props, theme }) => {
  const items = props.items || [];
  if (!items.length) return null;
  return (
    <Section className="bg-white" data-testid="org-block-leadership">
      <h2 id="leadership" className="text-3xl font-serif font-bold text-center mb-10" style={{ color: theme.primary }}>{props.heading}</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
        {items.map((p, i) => (
          <div key={i} className="text-center">
            {p.photo_url && (
              <img src={p.photo_url} alt={p.name} className="w-40 h-40 mx-auto rounded-full object-cover mb-4 ring-4" style={{ '--tw-ring-color': theme.accent }} />
            )}
            <h3 className="font-semibold text-lg" style={{ color: theme.primary }}>{p.name}</h3>
            {p.title && <p className="text-sm uppercase tracking-wide text-slate-500 mt-1">{p.title}</p>}
            {p.bio && <p className="text-sm text-slate-600 mt-3 px-2">{p.bio}</p>}
          </div>
        ))}
      </div>
    </Section>
  );
};

const ServiceTimesBlock = ({ props, theme }) => (
  <section id="service_times" className="py-16 px-4" style={{ backgroundColor: theme.primary }} data-testid="org-block-service-times">
    <div className="max-w-4xl mx-auto text-center text-white">
      <h2 className="text-3xl font-serif font-bold mb-10">{props.heading}</h2>
      <div className="grid sm:grid-cols-3 gap-6">
        {(props.items || []).map((s, i) => (
          <div key={i} className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
            <p className="text-xs uppercase tracking-widest" style={{ color: theme.accent }}>{s.day}</p>
            <p className="text-2xl font-serif font-bold mt-2">{s.time}</p>
            {s.service && <p className="text-sm text-white/80 mt-1">{s.service}</p>}
          </div>
        ))}
      </div>
    </div>
  </section>
);

const MinistriesBlock = ({ props, theme }) => (
  <Section className="bg-slate-50" data-testid="org-block-ministries">
    <h2 id="ministries" className="text-3xl font-serif font-bold text-center mb-10" style={{ color: theme.primary }}>{props.heading}</h2>
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {(props.items || []).map((m, i) => (
        <div key={i} className="bg-white rounded-lg p-6 shadow-sm border-l-4" style={{ borderColor: theme.accent }}>
          <h3 className="font-semibold text-lg mb-1" style={{ color: theme.primary }}>{m.name}</h3>
          {m.description && <p className="text-sm text-slate-600">{m.description}</p>}
        </div>
      ))}
    </div>
  </Section>
);

const SermonsBlock = ({ props, theme }) => {
  const items = props.items || [];
  if (!items.length) {
    return (
      <Section className="bg-white" data-testid="org-block-sermons-empty">
        <div id="sermons" />
        <h2 className="text-3xl font-serif font-bold text-center mb-4" style={{ color: theme.primary }}>{props.heading}</h2>
        <p className="text-center text-slate-500 text-sm">Sermons coming soon.</p>
      </Section>
    );
  }
  return (
    <Section className="bg-white" data-testid="org-block-sermons">
      <h2 id="sermons" className="text-3xl font-serif font-bold text-center mb-10" style={{ color: theme.primary }}>{props.heading}</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((s, i) => (
          <div key={i} className="rounded-lg overflow-hidden shadow-sm border bg-white">
            {s.thumbnail && <img src={s.thumbnail} alt={s.title} className="w-full h-40 object-cover" />}
            <div className="p-4">
              {s.date && <p className="text-xs text-slate-500">{s.date}</p>}
              <h3 className="font-semibold mt-1">{s.title}</h3>
              <div className="flex gap-2 mt-3 text-xs">
                {s.audio_url && <a href={s.audio_url} target="_blank" rel="noreferrer" className="px-2 py-1 rounded border hover:bg-slate-50">Audio</a>}
                {s.video_url && <a href={s.video_url} target="_blank" rel="noreferrer" className="px-2 py-1 rounded text-white" style={{ backgroundColor: theme.accent }}>Watch</a>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
};

const PAGE_SIZE = 24;

const REACTION_TYPES = [
  { key: 'candle', emoji: '🕯️', label: 'Light a candle' },
  { key: 'flower', emoji: '🌸', label: 'Lay a flower' },
  { key: 'heart',  emoji: '❤️', label: 'Send love' },
];

const TributeReactions = ({ siteSlug, tributeId, initial }) => {
  // Dedup per browser via localStorage so the same person can't spam-tap one
  // tribute, while still allowing a tap on every tribute.
  const storageKey = `tribute-reactions-${siteSlug}`;
  const [counts, setCounts] = React.useState(initial || {});
  const [reacted, setReacted] = React.useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem(storageKey) || '{}');
      return raw[tributeId] || {};
    } catch { return {}; }
  });

  const react = async (emoji) => {
    if (reacted[emoji]) {
      toast.info('You already reacted with this');
      return;
    }
    // Optimistic update
    setCounts((c) => ({ ...c, [emoji]: (c[emoji] || 0) + 1 }));
    setReacted((r) => {
      const next = { ...r, [emoji]: true };
      try {
        const raw = JSON.parse(localStorage.getItem(storageKey) || '{}');
        raw[tributeId] = next;
        localStorage.setItem(storageKey, JSON.stringify(raw));
      } catch { /* ignore */ }
      return next;
    });
    try {
      const res = await fetch(
        `${API_URL}/api/sites/public/${siteSlug}/tribute/${encodeURIComponent(tributeId)}/react?emoji=${emoji}`,
        { method: 'POST' },
      );
      if (!res.ok) throw new Error('Server rejected reaction');
      const data = await res.json();
      if (data.reactions) setCounts(data.reactions);
    } catch (err) {
      // Roll back optimistic if it failed.
      setCounts((c) => ({ ...c, [emoji]: Math.max(0, (c[emoji] || 1) - 1) }));
      setReacted((r) => {
        const next = { ...r };
        delete next[emoji];
        try {
          const raw = JSON.parse(localStorage.getItem(storageKey) || '{}');
          raw[tributeId] = next;
          localStorage.setItem(storageKey, JSON.stringify(raw));
        } catch { /* ignore */ }
        return next;
      });
      toast.error('Could not save your reaction');
    }
  };

  return (
    <div className="flex items-center gap-1 mt-2 -mb-1" data-testid={`tribute-reactions-${tributeId}`}>
      {REACTION_TYPES.map((r) => {
        const n = counts[r.key] || 0;
        const active = !!reacted[r.key];
        return (
          <button
            key={r.key}
            type="button"
            onClick={() => react(r.key)}
            title={r.label}
            data-testid={`tribute-react-${r.key}-${tributeId}`}
            className={`px-2 py-0.5 rounded-full text-xs flex items-center gap-1 transition border ${
              active
                ? 'bg-amber-50 border-amber-300 text-amber-800'
                : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            <span aria-hidden>{r.emoji}</span>
            {n > 0 && <span className="font-medium">{n}</span>}
          </button>
        );
      })}
    </div>
  );
};

const TributeBlock = ({ props, theme, siteSlug }) => {
  // Only show admin-approved tributes to the public. Legacy items without a
  // `status` field are treated as approved so existing seed content keeps
  // working. Sorted newest-first using sort_ts (set during import) or by
  // parsing the display date.
  const items = React.useMemo(() => {
    const approved = (props.items || []).filter(
      (t) => !t.status || t.status === 'approved',
    );
    const ts = (t) => {
      if (t.sort_ts) return t.sort_ts;
      const parsed = Date.parse(t.date || '');
      return Number.isNaN(parsed) ? 0 : parsed / 1000;
    };
    return [...approved].sort((a, b) => ts(b) - ts(a));
  }, [props.items]);
  const [visible, setVisible] = React.useState(PAGE_SIZE);
  const [name, setName] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || message.trim().length < 3) {
      toast.error('Please add your name and a short message.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/sites/public/${siteSlug}/tribute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), message: message.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Submission failed');
      toast.success(data.message || 'Thank you!');
      setName(''); setMessage(''); setSubmitted(true);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Section className="bg-slate-50" data-testid="org-block-tribute">
      <div className="text-center mb-10">
        <h2 id="tribute" className="text-3xl font-serif font-bold" style={{ color: theme.primary }}>{props.heading}</h2>
        {props.subhead && <p className="text-sm text-slate-500 mt-2">{props.subhead}</p>}
        {items.length > 0 && (
          <p className="text-xs text-slate-400 mt-1" data-testid="tribute-count">{items.length} tributes</p>
        )}
      </div>

      {items.length === 0 ? (
        <p className="text-center text-slate-500 text-sm mb-8">Be the first to leave a message.</p>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 gap-4 max-w-4xl mx-auto mb-6">
            {items.slice(0, visible).map((t, i) => (
              <div key={t.id || i} className="bg-white rounded p-4 shadow-sm border-l-2 flex flex-col" style={{ borderColor: theme.accent }}>
                <p className="text-sm text-slate-700 italic flex-1 whitespace-pre-line">&ldquo;{t.message}&rdquo;</p>
                <div className="flex items-center justify-between mt-3 pt-2 border-t border-slate-100">
                  <p className="text-xs font-medium text-slate-600">— {t.name}</p>
                  {t.date && (
                    <p className="text-xs text-slate-400" data-testid="tribute-date">{t.date}</p>
                  )}
                </div>
                {t.id && (
                  <TributeReactions
                    siteSlug={siteSlug}
                    tributeId={t.id}
                    initial={t.reactions}
                  />
                )}
              </div>
            ))}
          </div>
          {visible < items.length && (
            <div className="text-center mb-10">
              <Button
                type="button"
                variant="outline"
                onClick={() => setVisible((v) => v + PAGE_SIZE)}
                data-testid="tribute-load-more"
                style={{ borderColor: theme.accent, color: theme.primary }}
              >
                Load more tributes ({items.length - visible} remaining)
              </Button>
            </div>
          )}
        </>
      )}

      {/* Public submission form */}
      <form onSubmit={submit} className="max-w-xl mx-auto bg-white rounded-lg p-6 shadow-sm space-y-3" data-testid="tribute-form">
        <h3 className="font-semibold" style={{ color: theme.primary }}>Leave a Tribute</h3>
        {submitted ? (
          <p className="text-sm text-emerald-700">Thank you — your tribute is awaiting approval.</p>
        ) : (
          <>
            <input
              type="text"
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2"
              style={{ '--tw-ring-color': theme.accent }}
              data-testid="tribute-name"
            />
            <textarea
              placeholder="Share what God has done…"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              maxLength={2000}
              className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2"
              style={{ '--tw-ring-color': theme.accent }}
              data-testid="tribute-message"
            />
            <Button
              type="submit"
              disabled={submitting}
              className="text-white w-full"
              style={{ backgroundColor: theme.accent }}
              data-testid="tribute-submit"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {submitting ? 'Submitting…' : 'Submit Tribute'}
            </Button>
            <p className="text-xs text-slate-400 text-center">
              Submissions are reviewed by an administrator before they appear on this page.
            </p>
          </>
        )}
      </form>
    </Section>
  );
};

// ---------- Hospital-specific block renderers ----------

// MilestonesBlock — project construction phases with status pills.
const MILESTONE_STATUSES = {
  completed: { label: 'Completed', icon: CheckCircle2, color: 'emerald' },
  in_progress: { label: 'In Progress', icon: Wrench, color: 'amber' },
  upcoming: { label: 'Upcoming', icon: Clock, color: 'slate' },
};

const MilestonesBlock = ({ props, theme }) => {
  const items = props.items || [];
  if (!items.length) return null;
  const completedCount = items.filter((i) => i.status === 'completed').length;
  const pct = Math.round((completedCount / items.length) * 100);
  return (
    <Section className="bg-white" data-testid="org-block-milestones">
      <div className="text-center mb-2">
        <p className="text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: theme.accent }}>
          {props.kicker || 'Construction Progress'}
        </p>
        <h2 id="milestones" className="text-3xl font-bold mt-2" style={{ color: theme.primary }}>
          {props.heading || 'Project Milestones'}
        </h2>
        {props.subhead && <p className="text-sm text-slate-500 mt-2 max-w-2xl mx-auto">{props.subhead}</p>}
      </div>
      <div className="max-w-2xl mx-auto mb-10 mt-6">
        <div className="flex justify-between text-xs text-slate-500 mb-1">
          <span>{completedCount} of {items.length} phases complete</span>
          <span className="font-semibold" style={{ color: theme.primary }}>{pct}%</span>
        </div>
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full transition-all duration-1000" style={{ width: `${pct}%`, backgroundColor: theme.accent }} />
        </div>
      </div>
      <ol className="relative border-l-2 max-w-2xl mx-auto pl-6 space-y-6" style={{ borderColor: `${theme.accent}40` }}>
        {items.map((it, i) => {
          const meta = MILESTONE_STATUSES[it.status || 'upcoming'] || MILESTONE_STATUSES.upcoming;
          const Icon = meta.icon;
          return (
            <li key={i} className="relative" data-testid={`milestone-${i}`}>
              <span
                className={`absolute -left-[34px] top-1 w-5 h-5 rounded-full flex items-center justify-center text-white shadow-sm`}
                style={{ backgroundColor: meta.color === 'emerald' ? '#10b981' : meta.color === 'amber' ? '#f59e0b' : '#94a3b8' }}
              >
                <Icon className="h-3 w-3" />
              </span>
              <div className="flex items-baseline gap-2 flex-wrap">
                <h3 className="font-semibold" style={{ color: theme.primary }}>{it.title}</h3>
                {it.date && <span className="text-xs text-slate-500">· {it.date}</span>}
                <span
                  className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full"
                  style={{
                    backgroundColor: meta.color === 'emerald' ? '#d1fae5' : meta.color === 'amber' ? '#fef3c7' : '#f1f5f9',
                    color: meta.color === 'emerald' ? '#065f46' : meta.color === 'amber' ? '#92400e' : '#475569',
                  }}
                >
                  {meta.label}
                </span>
              </div>
              {it.description && <p className="text-sm text-slate-600 mt-1 whitespace-pre-line">{it.description}</p>}
            </li>
          );
        })}
      </ol>
      {props.cta_href && props.cta_label && (
        <div className="text-center mt-10">
          <Link to={props.cta_href}>
            <Button variant="outline" style={{ borderColor: theme.accent, color: theme.primary }}>
              {props.cta_label} <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
        </div>
      )}
    </Section>
  );
};

// QuickLinksBlock — 4-up grid of section anchors (Milestones / Gallery / Reports / Contact).
const QUICK_LINK_ICONS = {
  milestones: Wrench,
  gallery: ImageIcon,
  reports: FileText,
  contact: MessageSquare,
  stories: Heart,
  donate: HandHeart,
};

const QuickLinksBlock = ({ props, theme }) => {
  const items = props.items || [];
  if (!items.length) return null;
  return (
    <Section className="bg-slate-50" data-testid="org-block-quick-links">
      <div className={`grid sm:grid-cols-2 lg:grid-cols-${Math.min(items.length, 4)} gap-4 max-w-5xl mx-auto`}>
        {items.map((it, i) => {
          const Icon = QUICK_LINK_ICONS[it.icon] || ArrowRight;
          const isExternal = it.href && /^https?:/i.test(it.href);
          const Wrapper = isExternal
            ? ({ children }) => <a href={it.href} target="_blank" rel="noreferrer" className="block">{children}</a>
            : ({ children }) => <Link to={it.href || '#'} className="block">{children}</Link>;
          return (
            <Wrapper key={i}>
              <Card className="h-full hover:shadow-md transition group cursor-pointer">
                <CardContent className="p-6">
                  <div className="w-10 h-10 rounded-md flex items-center justify-center mb-3" style={{ backgroundColor: `${theme.accent}22`, color: theme.primary }}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-semibold mb-1" style={{ color: theme.primary }}>{it.title}</h3>
                  {it.description && <p className="text-sm text-slate-600">{it.description}</p>}
                  <span className="text-xs font-semibold mt-3 inline-flex items-center" style={{ color: theme.accent }}>
                    {it.cta_label || 'Learn more'}
                    <ArrowRight className="h-3 w-3 ml-1 group-hover:translate-x-0.5 transition" />
                  </span>
                </CardContent>
              </Card>
            </Wrapper>
          );
        })}
      </div>
    </Section>
  );
};

// FundingProgressBlock — progress bar pulling real $$ from /funding-progress.
const FundingProgressBlock = ({ props, theme, siteSlug }) => {
  const { data: prog = { raised: 0, donors: 0 } } = useQuery({
    queryKey: ['funding-progress', siteSlug],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/sites/public/${siteSlug}/funding-progress`);
      if (!res.ok) return { raised: 0, donors: 0 };
      return res.json();
    },
    staleTime: 30_000,
  });
  // Admin can pin a custom raised number (e.g. include offline gifts).
  const raised = Number(props.override_raised || 0) > 0
    ? Number(props.override_raised)
    : (prog.raised || 0);
  const goal = Number(props.goal || 0);
  const pct = goal > 0 ? Math.min(100, Math.round((raised / goal) * 100)) : 0;
  const currency = (props.currency || 'USD').toUpperCase();
  const fmt = (n) => fmtCurrency(n, currency, { decimals: 0, showCode: true });
  return (
    <Section className="bg-white" data-testid="org-block-funding-progress">
      <div className="text-center mb-6">
        <p className="text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: theme.accent }}>
          {props.kicker || 'Funding Progress'}
        </p>
        <h2 className="text-3xl font-bold mt-2" style={{ color: theme.primary }}>{props.heading || 'Help Us Reach Our Goal'}</h2>
        {props.subhead && <p className="text-sm text-slate-500 mt-2 max-w-2xl mx-auto">{props.subhead}</p>}
      </div>
      <div className="max-w-3xl mx-auto bg-slate-50 border rounded-2xl p-8 shadow-sm">
        <div className="flex items-end justify-between flex-wrap gap-2 mb-2">
          <div>
            <p className="text-xs uppercase text-slate-500">Raised</p>
            <p className="text-3xl font-bold" style={{ color: theme.primary }}>
              {fmt(raised)}
            </p>
          </div>
          {goal > 0 && (
            <div className="text-right">
              <p className="text-xs uppercase text-slate-500">Goal</p>
              <p className="text-xl font-semibold text-slate-700">{fmt(goal)}</p>
            </div>
          )}
        </div>
        <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
          <div
            className="h-full transition-all duration-1000"
            style={{ width: `${pct}%`, backgroundColor: theme.accent }}
            data-testid="funding-progress-bar"
          />
        </div>
        <div className="flex justify-between text-xs text-slate-500 mt-2">
          <span>{pct}% funded</span>
          {prog.donors > 0 && <span>{prog.donors} donor{prog.donors === 1 ? '' : 's'} so far</span>}
        </div>
        {props.cta_label && props.cta_href && (
          <div className="text-center mt-6">
            <Link to={props.cta_href}>
              <Button size="lg" className="text-white" style={{ backgroundColor: theme.accent }}>
                <HandHeart className="h-4 w-4 mr-2" /> {props.cta_label}
              </Button>
            </Link>
          </div>
        )}
      </div>
    </Section>
  );
};

// NewsletterBlock — email signup with optional category checkboxes.
const NewsletterBlock = ({ props, theme, siteSlug }) => {
  const [email, setEmail] = React.useState('');
  const [name, setName] = React.useState('');
  const [cats, setCats] = React.useState({});
  const [loading, setLoading] = React.useState(false);
  const [subbed, setSubbed] = React.useState(false);
  const categories = props.categories || [];

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/sites/public/${siteSlug}/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          name,
          categories: Object.keys(cats).filter((k) => cats[k]),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Subscribe failed');
      toast.success(data.message || 'Subscribed!');
      setSubbed(true);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Section className="bg-slate-100" data-testid="org-block-newsletter">
      <div className="grid lg:grid-cols-2 gap-8 items-center max-w-5xl mx-auto">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: theme.accent }}>
            {props.kicker || 'Stay Connected'}
          </p>
          <h2 className="text-3xl font-bold mt-2" style={{ color: theme.primary }}>{props.heading || 'Join Our Community'}</h2>
          {props.body && <p className="text-slate-600 mt-3">{props.body}</p>}
          {props.bullets && props.bullets.length > 0 && (
            <ul className="mt-4 space-y-2 text-sm text-slate-700">
              {props.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: theme.accent }} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="bg-white rounded-xl shadow-sm p-6 border" data-testid="newsletter-form">
          <Bell className="h-6 w-6 mb-2" style={{ color: theme.accent }} />
          <h3 className="font-semibold text-lg" style={{ color: theme.primary }}>{props.form_heading || 'Stay Updated'}</h3>
          <p className="text-xs text-slate-500 mb-4">{props.form_sub || 'Get the latest updates delivered to your inbox.'}</p>
          {subbed ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded p-4 text-sm text-emerald-800">
              You&apos;re on the list! Watch your inbox for our next update.
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <input
                type="text"
                placeholder="Your name (optional)"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full border rounded px-3 py-2 text-sm"
                data-testid="newsletter-name"
              />
              <input
                type="email"
                required
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border rounded px-3 py-2 text-sm"
                data-testid="newsletter-email"
              />
              {categories.length > 0 && (
                <div className="space-y-1 pt-1">
                  <p className="text-xs font-semibold text-slate-600">Customize your updates</p>
                  {categories.map((c) => (
                    <label key={c} className="flex items-center gap-2 text-xs text-slate-600">
                      <input
                        type="checkbox"
                        checked={!!cats[c]}
                        onChange={(e) => setCats({ ...cats, [c]: e.target.checked })}
                        data-testid={`newsletter-cat-${c.replace(/\s+/g, '-').toLowerCase()}`}
                      />
                      {c}
                    </label>
                  ))}
                </div>
              )}
              <Button
                type="submit"
                disabled={loading}
                className="w-full text-white"
                style={{ backgroundColor: theme.accent }}
                data-testid="newsletter-submit"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Subscribe to Updates
              </Button>
              <p className="text-[10px] text-slate-400 text-center">We respect your privacy. Unsubscribe anytime.</p>
            </form>
          )}
        </div>
      </div>
    </Section>
  );
};

// PatientStoriesBlock — testimonials w/ moderation + public submission.
const PatientStoriesBlock = ({ props, theme, siteSlug }) => {
  const items = React.useMemo(() => (
    (props.items || []).filter((s) => !s.status || s.status === 'approved')
  ), [props.items]);
  const [name, setName] = React.useState('');
  const [title, setTitle] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [photo, setPhoto] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || message.trim().length < 3) {
      toast.error('Please share your name and a short story.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/sites/public/${siteSlug}/patient-story`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, title, message, photo_url: photo }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Submission failed');
      toast.success(data.message || 'Thank you!');
      setName(''); setTitle(''); setMessage(''); setPhoto(''); setSubmitted(true);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Section className="bg-white" data-testid="org-block-patient-stories">
      <div className="text-center mb-8">
        <p className="text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: theme.accent }}>
          {props.kicker || 'Patient Stories'}
        </p>
        <h2 id="patient_stories" className="text-3xl font-bold mt-2" style={{ color: theme.primary }}>{props.heading || 'Healing in the Community'}</h2>
        {props.subhead && <p className="text-sm text-slate-500 mt-2 max-w-2xl mx-auto">{props.subhead}</p>}
      </div>
      {items.length > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto mb-10">
          {items.slice(0, 9).map((s, i) => (
            <div key={s.id || i} className="bg-slate-50 rounded-lg p-5 border-t-4 flex flex-col" style={{ borderColor: theme.accent }}>
              {s.photo_url && (
                <img src={s.photo_url} alt={s.name} className="w-16 h-16 rounded-full object-cover mb-3 ring-2 ring-white shadow" />
              )}
              <p className="text-sm text-slate-700 italic flex-1 whitespace-pre-line">&ldquo;{s.message}&rdquo;</p>
              <div className="mt-3 pt-3 border-t border-slate-200">
                <p className="font-semibold text-sm" style={{ color: theme.primary }}>{s.name}</p>
                {s.title && <p className="text-xs text-slate-500">{s.title}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
      {props.allow_submissions !== false && (
        <form onSubmit={submit} className="max-w-xl mx-auto bg-slate-50 rounded-lg p-6 border space-y-3" data-testid="patient-story-form">
          <h3 className="font-semibold" style={{ color: theme.primary }}>Share Your Story</h3>
          {submitted ? (
            <p className="text-sm text-emerald-700">Thank you — your story is awaiting review.</p>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 gap-2">
                <input
                  type="text"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full border rounded px-3 py-2 text-sm"
                  data-testid="patient-story-name"
                />
                <input
                  type="text"
                  placeholder="Caption (e.g. Mother of two)"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full border rounded px-3 py-2 text-sm"
                  data-testid="patient-story-title"
                />
              </div>
              <textarea
                placeholder="Share how the hospital helped your family…"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                className="w-full border rounded px-3 py-2 text-sm"
                data-testid="patient-story-message"
              />
              <MediaUpload value={photo} onChange={setPhoto} purpose="photo" label="Photo (optional)" testId="patient-story-photo" previewClassName="h-20" />
              <Button
                type="submit"
                disabled={loading}
                className="text-white w-full"
                style={{ backgroundColor: theme.accent }}
                data-testid="patient-story-submit"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Share My Story
              </Button>
              <p className="text-xs text-slate-400 text-center">Stories are reviewed before they appear.</p>
            </>
          )}
        </form>
      )}
    </Section>
  );
};

// AppointmentsBlock — appointment request form (patient name + phone + reason).
const AppointmentsBlock = ({ props, theme, siteSlug }) => {
  const [form, setForm] = React.useState({
    name: '', phone: '', email: '', department: '', preferred_date: '', reason: '',
  });
  const [loading, setLoading] = React.useState(false);
  const [done, setDone] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || form.reason.length < 3) {
      toast.error('Name, phone and reason are required.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/sites/public/${siteSlug}/appointment-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not submit');
      toast.success(data.message || 'Thank you!');
      setDone(true);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const departments = props.departments || ['General Consultation', 'Maternal Health', 'Emergency', 'Pharmacy'];

  return (
    <Section className="bg-slate-50" data-testid="org-block-appointments">
      <div className="grid lg:grid-cols-2 gap-8 items-start max-w-5xl mx-auto">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: theme.accent }}>
            {props.kicker || 'Book an Appointment'}
          </p>
          <h2 id="appointments" className="text-3xl font-bold mt-2" style={{ color: theme.primary }}>{props.heading || 'Request an Appointment'}</h2>
          {props.body && <p className="text-slate-600 mt-3 whitespace-pre-line">{props.body}</p>}
          <div className="mt-5 space-y-2 text-sm text-slate-700">
            <p className="flex items-center gap-2"><Stethoscope className="h-4 w-4" style={{ color: theme.accent }} /> Consultations Mon–Sun, 10am–6pm</p>
            <p className="flex items-center gap-2"><Phone className="h-4 w-4" style={{ color: theme.accent }} /> {props.contact_phone || '+263 77 123 4567'}</p>
            <p className="flex items-center gap-2"><Hand className="h-4 w-4" style={{ color: theme.accent }} /> Walk-ins welcome for emergencies</p>
          </div>
        </div>
        <form onSubmit={submit} className="bg-white rounded-xl shadow-sm p-6 border space-y-3" data-testid="appointment-form">
          {done ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded p-4 text-sm text-emerald-800">
              Request received. Our team will call you back shortly.
            </div>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 gap-3">
                <input
                  required
                  placeholder="Full name *"
                  value={form.name}
                  onChange={(e) => set('name', e.target.value)}
                  className="border rounded px-3 py-2 text-sm"
                  data-testid="appt-name"
                />
                <input
                  required
                  placeholder="Phone *"
                  value={form.phone}
                  onChange={(e) => set('phone', e.target.value)}
                  className="border rounded px-3 py-2 text-sm"
                  data-testid="appt-phone"
                />
              </div>
              <input
                type="email"
                placeholder="Email (optional)"
                value={form.email}
                onChange={(e) => set('email', e.target.value)}
                className="w-full border rounded px-3 py-2 text-sm"
                data-testid="appt-email"
              />
              <div className="grid sm:grid-cols-2 gap-3">
                <select
                  value={form.department}
                  onChange={(e) => set('department', e.target.value)}
                  className="border rounded px-3 py-2 text-sm bg-white"
                  data-testid="appt-department"
                >
                  <option value="">Department…</option>
                  {departments.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
                <input
                  type="date"
                  value={form.preferred_date}
                  onChange={(e) => set('preferred_date', e.target.value)}
                  className="border rounded px-3 py-2 text-sm"
                  data-testid="appt-date"
                />
              </div>
              <textarea
                required
                placeholder="Reason for visit *"
                rows={3}
                value={form.reason}
                onChange={(e) => set('reason', e.target.value)}
                className="w-full border rounded px-3 py-2 text-sm"
                data-testid="appt-reason"
              />
              <Button
                type="submit"
                disabled={loading}
                className="w-full text-white"
                style={{ backgroundColor: theme.accent }}
                data-testid="appt-submit"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Request Appointment
              </Button>
            </>
          )}
        </form>
      </div>
    </Section>
  );
};


// ============== Block dispatcher ==============
const BLOCK_RENDERERS = {
  hero: HeroBlock,
  about: AboutBlock,
  services: ServicesBlock,
  doctors: DoctorsBlock,
  patient_info: PatientInfoBlock,
  news: NewsBlock,
  gallery: GalleryBlock,
  donate: DonateBlock,
  contact: ContactBlock,
  footer: FooterBlock,
  // Church-specific
  leadership: LeadershipBlock,
  service_times: ServiceTimesBlock,
  ministries: MinistriesBlock,
  sermons: SermonsBlock,
  tribute: TributeBlock,
  events: EventsBlock,
  livestream: LivestreamBlock,
  prayer_requests: PrayerRequestsBlock,
  // Hospital-specific
  milestones: MilestonesBlock,
  quick_links: QuickLinksBlock,
  funding_progress: FundingProgressBlock,
  newsletter: NewsletterBlock,
  patient_stories: PatientStoriesBlock,
  appointments: AppointmentsBlock,
};

// ============== Page ==============
const detectSlugFromHost = () => {
  if (typeof window === 'undefined') return null;
  const host = window.location.host.toLowerCase();
  // e.g. abmhc.faithworldministries.com → "abmhc"
  // Skip generic platform hosts so the main app isn't hijacked
  const ignore = ['localhost', 'preview.emergentagent.com', 'faithworldministries.com'];
  const parts = host.split('.');
  if (parts.length < 3) return null;            // bare apex, no subdomain
  if (ignore.includes(parts.slice(1).join('.'))) {
    return parts[0];                              // strip the platform suffix
  }
  return null;
};

const OrgSitePage = ({ slug: propSlug }) => {
  const params = useParams();
  const slug = (propSlug || params.slug || detectSlugFromHost() || '').toLowerCase();

  const { data: site, isLoading, error } = useQuery({
    queryKey: ['org-site', slug],
    queryFn: async () => {
      if (!slug) throw new Error('No site slug');
      const res = await fetch(`${API_URL}/api/sites/public/${slug}`, { cache: 'no-store' });
      if (!res.ok) throw new Error('Site not found');
      return res.json();
    },
    retry: false,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
  });

  React.useEffect(() => {
    if (site?.seo?.title) document.title = site.seo.title;
  }, [site]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="org-site-loading">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error || !site) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="org-site-404">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="py-8 text-center">
            <h2 className="text-xl font-bold mb-2">Site Not Found</h2>
            <p className="text-sm text-slate-500 mb-4">We couldn&apos;t find a site at <code>{slug || 'unknown'}</code>.</p>
            <Link to="/"><Button variant="outline">Go Home</Button></Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const theme = {
    primary: site.theme?.primary || '#1c3e6e',
    accent: site.theme?.accent || '#f59052',
    text: site.theme?.text || '#0f172a',
  };

  const navLinks = Array.isArray(site.nav_links) && site.nav_links.length
    ? site.nav_links
    : [
        { label: 'Home', href: '#hero' },
        { label: 'About', href: '#about' },
        { label: 'Services', href: '#services' },
        { label: 'Team', href: '#doctors' },
        { label: 'Gallery', href: '#gallery' },
        { label: 'Contact', href: '#contact' },
      ];

  const donateBlock = (site.blocks || []).find((b) => b.type === 'donate');
  const donateHref = donateBlock?.props?.cta_href || '/campaigns/abmhc';

  return (
    <div className="min-h-screen bg-white" data-testid="org-site-page" style={{ '--site-primary': theme.primary, '--site-accent': theme.accent }}>
      {/* Top thin bar */}
      <div className="text-white text-sm" style={{ backgroundColor: theme.primary }} data-testid="org-top-bar">
        <div className="max-w-6xl mx-auto px-6 py-2 flex justify-end">
          <Link to={donateHref} className="hover:opacity-80 transition flex items-center gap-1">
            Support Our Mission <span aria-hidden>›</span>
          </Link>
        </div>
      </div>

      {/* Main nav header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30" data-testid="org-nav-header">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 group">
            <div
              className="w-10 h-10 rounded-md flex items-center justify-center text-white font-bold font-serif text-lg"
              style={{ backgroundColor: theme.primary }}
            >
              {(site.logo_letter || (site.name || 'A').charAt(0)).toUpperCase()}
            </div>
            <div className="leading-tight">
              <p className="font-serif font-bold text-lg" style={{ color: theme.primary }}>{site.name}</p>
              {site.tagline && <p className="text-[11px] text-slate-500 -mt-0.5">{site.tagline}</p>}
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm font-medium text-slate-700">
            {navLinks.map((l, i) => (
              <a key={i} href={l.href} className="hover:opacity-70 transition" data-testid={`nav-${l.label.toLowerCase()}`}>{l.label}</a>
            ))}
          </nav>
          <Link to={donateHref}>
            <Button className="text-white px-5" style={{ backgroundColor: theme.accent }} data-testid="org-nav-donate-btn">
              Donate Now
            </Button>
          </Link>
        </div>
      </header>

      {(site.blocks || []).map((block) => {
        const Renderer = BLOCK_RENDERERS[block.type];
        if (!Renderer) return null;
        return <Renderer key={block.id} props={block.props || {}} theme={theme} siteSlug={slug} />;
      })}

      {/* ABMHC-only: CMS-controlled featured video hero.
          Renders nothing when disabled/unpublished. */}
      {slug === 'abmhc' && <ABMHCFeaturedMedia />}
    </div>
  );
};

export default OrgSitePage;
